/**
 * SDMS Result Validation — "Trend" tab (third top tab, after Batch Results and
 * Check Points).
 *
 * WHY THIS TAB EXISTS
 * -------------------
 * Batch Results answers "are this batch's numbers internally valid?".  Check
 * Points answers "was the run performed correctly?".  Neither answers the
 * question a reviewer actually signs their name to: *"is this result normal for
 * this location, and is the location drifting toward its MCL?"*.  The Trend tab
 * answers that by charting the location's APPROVED history around the batch
 * under review, so a single in-spec-looking result that continues a rising
 * 12-month climb is visible before validation, not after a compliance report.
 *
 * TWO MODES (the toggle on the right of the picker row)
 * ----------------------------------------------------
 *  1. "One analyte · many sites"  (default)
 *       Pickers: Parameter, then Location(-set).
 *       Every series is the SAME analyte, so they share one native-unit axis and
 *       ONE MCL line (e.g. TTHM, MCL 80 µg/L).  Answers "which of my sites is
 *       worst for this analyte?"
 *       Table: Sampling Location | Latest | 12-mo mean | LRAA | % of MCL | Status
 *
 *  2. "One site · many analytes"
 *       Pickers: Location, then Parameter(-set)  ← deliberately swapped: the
 *       first picker is always the dimension being held constant.
 *       Analytes carry DIFFERENT units and limits (TTHM 80 µg/L, Nitrate
 *       10 mg/L, Arsenic 10 µg/L …), so plotting native values on one axis
 *       would be meaningless.  Each series is normalised to **% of its own
 *       MCL**, which makes the 100% line the regulatory limit for every series
 *       at once.  Answers "at this site, which analyte is closest to its limit?"
 *       Table: Analyte | Latest | Units | MCL | % of MCL | Status
 *
 * DERIVED FIGURES (all computed here from the approved points the API returns)
 * ---------------------------------------------------------------------------
 *   • Latest      — the most recent monthly value in the window.
 *   • 12-mo mean  — arithmetic mean of every point in the window.
 *   • LRAA        — locational running annual average: the mean of the most
 *                   recent FOUR monitoring results at that location (product
 *                   decision 2026-08-01).  Standard practice for TTHM/HAA5 is
 *                   four consecutive quarterly samples; using "last four
 *                   results" keeps the figure correct when a site is sampled on
 *                   a non-quarterly cadence, and degrades gracefully (a site
 *                   with 2 points averages 2, flagged via `lraaPartial`).
 *   • % of MCL    — LATEST ÷ MCL × 100.  Deliberately the latest value, not the
 *                   LRAA: the reviewer is validating *today's* result, and this
 *                   column is what drives the Status pill next to it.
 *   • Status      — In range < 80% · Watch 80–99% · Exceeds ≥ 100%.  80% is the
 *                   conventional operational trigger level (product decision
 *                   2026-08-01).
 *
 * DATA
 * ----
 * GET /api/v1/analysis/result-validation/{batchOid}/trend?months=12 — approved
 * results only (ITrendReader), scoped to the sampling locations of the batch
 * under review.  ONE fetch feeds BOTH modes, so switching mode or picker pivots
 * in-memory and never leaves validation (no refetch, no spinner).
 *
 * When that returns nothing (a batch whose sites have no approved history yet),
 * the tab falls back to a clearly-banner-labelled DEMONSTRATION series rather
 * than a blank card — the same seed-fallback precedent the Batch Results tab
 * uses.  The banner is deliberately loud: these numbers must never be mistaken
 * for compliance data.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
// No <Legend> — it renders inside the SVG; the legend is plain HTML below the
// chart box so the box holds only the plot + its axis caption (see render).
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Customized,
} from 'recharts';
import { api } from '../../api/client';
// Shared date helper (§14b.6) — the custom hover tooltip renders the exact
// sample/collection date as MM/DD/YYYY, never inline date formatting.
import { fmtLocalDate } from '../../utils/datetime';
// Shared notification service (CLAUDE.md §18.6) — used to warn when the reviewer's
// row selection can't be trended as asked (multiple parameters, QC rows with no
// location, no reviewed history). Never a bespoke toast.
import { useNotify } from '../../components/notifications';
// Shared column model + header menu for hand-rolled tables (Hide · Show All ·
// Column Chooser · drag-reorder · resize · persistence) — the SAME hooks the Data
// Center ModuleListView uses, so the Trend summary table gets the identical
// right-click column features as every other grid (client 2026-08-05).
import { useColumnManager, useHeaderMenu } from '../../components/AppDataGrid';
// The compliance math (LRAA, % of MCL, Watch/Exceeds) lives in a pure module so
// it is unit-tested directly rather than only through the chart — see
// trendMath.test.ts.
import {
  EXCEED_PCT,
  LRAA_WINDOW,
  WATCH_PCT,
  axisTicks,
  chooseAxis,
  monthKeyOf,
  monthLong,
  monthSequence,
  monthShort,
  monthSlash,
  pctOfMcl,
  r0,
  r1,
  rollUp,
  roundForChart,
  statusOf,
  trimZeros,
  type TrendPointVm as Point,
  type TrendStat as Stat,
  type TrendStatus as Status,
} from '../trend/trendMath';

// ── API payload (matches Application/Analysis/Trend/ITrendReader.TrendPoint) ──
interface ApiTrendPoint {
  siteName: string | null;
  parameter: string | null;
  month: string | null;        // 'yyyy-MM' — bucketed in SQL, timezone-proof
  collectDate: string | null;
  value: number | null;
  units: string | null;
  mcl: number | null;
}

export type TrendMode = 'analyte' | 'site';

/** Per-point metadata the custom hover tooltip needs but the plotted chart rows
 *  don't carry: the exact collection date, the native (un-normalised) value and
 *  its unit.  Keyed `${month}|${seriesKey}` (client 2026-08-04). */
type PointMeta = { v: number; cd: string; nv: number; units: string };

interface Props {
  /** SpreadSheetEntry_AnalyticalBatch.Oid of the batch being validated. */
  batchOid: string;
  /** Batch's test name — only used to title the empty/demo state. */
  testName?: string | null;
  /** Site + analyte of the row selected in Batch Results.  The tab OPENS
   *  scoped to that row (per the reference design) instead of an arbitrary
   *  first entry, so Trend continues the reviewer's train of thought. */
  focusSite?: string | null;
  focusAnalyte?: string | null;
  /** True when the reviewer has TICKED one or more Batch Results rows.  With a
   *  selection the Trend view AUTO-SCOPES to those rows' sampling locations +
   *  parameter and displays immediately; with none, the pickers drive it
   *  manually (client requirement 2026-08-03). */
  hasSelection?: boolean;
  /** Distinct sampling locations of the selected rows (QC rows without a site
   *  are excluded — they can't be trended). */
  selectedSites?: string[];
  /** Distinct parameters of the selected rows.  Normally length 1 (a batch is a
   *  single test); >1 means the reviewer ticked rows across parameters, which is
   *  warned about since Trend charts one parameter at a time. */
  selectedAnalytes?: string[];
  /** Labels of selected rows that carry NO sampling location (QC samples such as
   *  Method Blank / CCV) — surfaced in the "nothing to trend" warning. */
  sitelessSelected?: string[];
  /** The batch's OWN parameter name(s) — its distinct SpreadSheetEntry_SampleInfo
   *  ParameterNames. Scopes the analyte-mode "Parameter" dropdown to just these, so
   *  a single-test batch (e.g. Alkalinity) never offers the sites' OTHER tests
   *  (Conductivity) as a trend parameter (client 2026-08-06). Empty ⇒ unknown
   *  (demo/seed fallback) ⇒ the dropdown falls back to every analyte with history. */
  batchParameters?: string[];
  /** True when the batch's own results are non-numeric (e.g. Colilert-18
   *  Present / Absent).  Such a batch can NEVER have a numeric trend, so the
   *  empty state says that outright instead of implying missing history. */
  qualitative?: boolean;
  /** Grid corner actions (Export · Maximize) supplied by the parent, rendered at
   *  the RIGHT end of this tab's own controls row so the Trend tab carries the
   *  same pair as the Batch Results / Check Points grids (client 2026-08-17). */
  actions?: ReactNode;
  /** Reports the SUMMARY TABLE's current projection (visible columns in the
   *  user's order + the displayed cell text) so the parent's shared <ExportMenu>
   *  can write it. Fires whenever the visible columns or summary rows change;
   *  the chart itself is not exportable, the table under it is. */
  onExportData?: (data: { headers: string[]; rows: string[][] }) => void;
}

/** Rolling window the tab requests AND charts, in months.  The x-axis always
 *  spans the whole window so a sparsely-sampled site reads as gaps in the
 *  record rather than as a deceptively full short chart.  Design intent is the
 *  past 12 months, per sampling location (client-confirmed 2026-08-06); the
 *  historic-archive union in TrendReader fills the older months WITHIN this
 *  window (the bi-monthly archive results the live table lacks) rather than
 *  widening the window. */
const WINDOW_MONTHS = 12;

/** Above this many series the end-of-line labels collide into an unreadable
 *  stack, so they are dropped and the legend carries the naming instead. */
const MAX_END_LABELS = 5;

/** Location-picker sentinel: chart ONLY the sampling locations of the rows the
 *  reviewer ticked in Batch Results (as opposed to '' = every site, or a single
 *  named site). Used when the selection spans more than one location. */
const SELECTED_SITES = '__selected__';

// Series palette — opens on the reference design's site ordering
// (D1 orange · D2 blue · D3 purple · D4 green), then extends.  Widened from 8
// to 12 because a 10-site view WRAPPED the palette and drew two different
// locations in the SAME colour, which no amount of chart tidying can rescue.
const PALETTE = [
  '#e8670a', '#2f6fd0', '#7b4bc9', '#22a35b',
  '#c2185b', '#0e8a8a', '#946200', '#5566d6',
  '#b02a8f', '#3d7a1f', '#0b6bb5', '#a33b12',
];
const MCL_COLOR = '#d92d20';

/** Palette lookup that always yields a string (strict indexed access). */
const seriesColor = (i: number): string => PALETTE[i % PALETTE.length] ?? MCL_COLOR;

/** Past one full palette cycle, colour alone repeats — the second lap is drawn
 *  dashed and the third dotted so every series stays uniquely identifiable. */
function seriesDash(i: number): string | undefined {
  const lap = Math.floor(i / PALETTE.length);
  return lap === 0 ? undefined : lap === 1 ? '7 4' : '2 3';
}

const STATUS_CLASS: Record<Status, string> = {
  'In range': 'tr-ok',
  Watch: 'tr-watch',
  Exceeds: 'tr-bad',
  '—': 'tr-na',
};

// Balanced DEFAULT column widths for the summary table (client 2026-08-06:
// "balance the column width"). Keyed by column KEY (not position) so drag-reorder
// keeps each column's width; paired with table-layout:fixed in .tr-tbl the six
// visible columns share the full width evenly — the name column marginally wider,
// every figure column equal. A user resize (colWidth) still overrides these.
// Each mode shows 6 columns and its widths sum to 100%.
const TREND_COL_WIDTH: Record<string, string> = {
  label: '20%',    // SamplingLocation / Analyte — text names, slightly wider
  latest: '16%',
  mean12: '16%',
  lraa: '16%',
  units: '16%',
  mcl: '16%',
  pct: '16%',
  status: '16%',
};

// NO DEMONSTRATION FALLBACK.
// An earlier version synthesised a 4-site x 5-analyte sample series when the API
// returned nothing, behind a "Demonstration data" banner.  It was withdrawn
// 2026-08-01 after it fired on a Colilert-18 Presence/Absence (microbiology)
// batch and drew TTHM / Nitrate / ARSENIC at "Site D1-D4 — Distribution" —
// analytes and locations that appear nowhere in that batch (its real content is
// Total Coliform + E. coli, Present/Absent, at sites NE4-1S…NE4-15S) — complete
// with green "In range" pills and % of MCL figures.  In a regulated LIMS a
// banner is not sufficient protection against fabricated compliance data on
// screen: an empty chart that says why is always safer than a full one that is
// not about this batch.

export function SdmsTrendTab({
  batchOid,
  testName,
  focusSite,
  focusAnalyte,
  qualitative,
  hasSelection = false,
  selectedSites = [],
  selectedAnalytes = [],
  sitelessSelected = [],
  batchParameters = [],
  actions,
  onExportData,
}: Props) {
  const notify = useNotify();
  const [points, setPoints] = useState<Point[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  // Fires the selection warnings once per mount (the tab remounts every time it
  // is opened, so each open re-evaluates the current selection).
  const warnedRef = useRef(false);

  const [mode, setMode] = useState<TrendMode>('analyte');
  // '' = the "All …" aggregate option in each picker.
  const [analyte, setAnalyte] = useState<string>('');
  const [site, setSite] = useState<string>('');
  /** The focus row's site — remembered so flipping to mode 2 lands on the
   *  location the reviewer came from, even though mode 1 shows all sites. */
  const [preferredSite, setPreferredSite] = useState<string>('');
  /** The analyte the tab opened on — restored when returning to mode 1, where
   *  the empty "all analytes" selection of mode 2 is not a valid choice. */
  const [preferredAnalyte, setPreferredAnalyte] = useState<string>('');
  // Overlap controls.  Ten sites inside a 15-mg/L band cannot be told apart by
  // colour alone however the chart is drawn, so the legend doubles as a filter:
  // HOVER dims every other series, CLICK hides one entirely.  Both are chart
  // affordances only — the summary table below always lists every series, so
  // hiding a line can never hide a compliance figure.
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());
  const [hoverSeries, setHoverSeries] = useState<string | null>(null);
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    api
      .get(`/api/v1/analysis/result-validation/${batchOid}/trend`, { params: { months: WINDOW_MONTHS } })
      .then((res) => {
        if (!alive) return;
        const raw: ApiTrendPoint[] = res.data?.data ?? [];
        const clean: Point[] = raw
          .filter((p) => p.siteName && p.parameter && p.month && p.value !== null)
          .map((p) => ({
            site: p.siteName as string,
            analyte: p.parameter as string,
            month: p.month as string,
            // Collect timestamp — used to pick the LATEST reading within a month
            // so the chart and the summary table agree even with several samples
            // in the same month (falls back to the month bucket when absent).
            collectDate: p.collectDate ?? null,
            value: Number(p.value),
            units: p.units ?? '',
            mcl: p.mcl === null || p.mcl === undefined ? null : Number(p.mcl),
          }));
        setPoints(clean);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.error?.message ?? e?.message ?? 'Request failed');
        setPoints([]);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [batchOid, reloadTick]);

  // ── dimension lists ───────────────────────────────────────────────────────
  const sites = useMemo(
    () => Array.from(new Set((points ?? []).map((p) => p.site))).sort((a, b) => a.localeCompare(b)),
    [points],
  );
  const analytes = useMemo(
    () => Array.from(new Set((points ?? []).map((p) => p.analyte))).sort((a, b) => a.localeCompare(b)),
    [points],
  );
  /** Analytes carrying a numeric MCL — the "All regulated analytes" set.  An
   *  unregulated parameter has no 100% line, so it cannot share the % axis. */
  const regulated = useMemo(() => {
    const withMcl = new Set((points ?? []).filter((p) => p.mcl !== null).map((p) => p.analyte));
    return analytes.filter((a) => withMcl.has(a));
  }, [points, analytes]);
  // The analyte-mode "Parameter" dropdown lists ONLY the batch's OWN parameter(s)
  // (client 2026-08-06: an Alkalinity batch must not offer Conductivity, which
  // belongs to the sites' OTHER tests). Scoped to batchParameters ∩ the analytes
  // that actually have trend history; falls back to the full list when the batch's
  // params are unknown or none has history, so the picker is never empty. ONLY this
  // dropdown is scoped — "one site · many analytes" keeps the site's full cross-test
  // regulated set (that comparison is the whole point of that mode).
  const pickerAnalytes = useMemo(() => {
    if (!batchParameters || batchParameters.length === 0) return analytes;
    const want = new Set(batchParameters.map((p) => p.trim().toLowerCase()));
    const scoped = analytes.filter((a) => want.has(a.trim().toLowerCase()));
    return scoped.length > 0 ? scoped : analytes;
  }, [analytes, batchParameters]);

  // The trend-data site names that correspond to the reviewer's ticked rows.
  // Matched fuzzily (case-insensitive substring either way) because the Batch
  // Results grid's siteName and the trend reader's site string can differ
  // slightly — same tolerance the focus matcher uses.  Empty when the selection
  // has no location (QC rows) or none of the selected sites has reviewed history.
  const matchedSelectedSites = useMemo(() => {
    if (!hasSelection || selectedSites.length === 0) return [] as string[];
    const norm = (s: string) => s.trim().toLowerCase();
    return sites.filter((s) =>
      selectedSites.some((sel) => {
        const a = norm(s);
        const b = norm(sel);
        return b !== '' && (a.includes(b) || b.includes(a));
      }),
    );
  }, [hasSelection, selectedSites, sites]);

  // The pickers must offer only what the OTHER dimension actually has data for,
  // and their "All …(n)" counts must match the number of rows the summary table
  // will show.  Listing all 10 sites while the chosen analyte exists at only 8
  // both overstated the count and offered 2 sites that render an empty chart.
  const sitesForAnalyte = useMemo(
    () =>
      Array.from(new Set((points ?? []).filter((p) => p.analyte === analyte).map((p) => p.site)))
        .sort((a, b) => a.localeCompare(b)),
    [points, analyte],
  );
  const regulatedAtSite = useMemo(
    () =>
      Array.from(
        new Set((points ?? []).filter((p) => p.site === site && p.mcl !== null).map((p) => p.analyte)),
      ).sort((a, b) => a.localeCompare(b)),
    [points, site],
  );
  // Open scoped to the Batch Results row the reviewer selected, falling back to
  // the first available dimension.  Runs once per data load.
  //
  // NOTE the asymmetry, which is the point of mode 1: the focus row picks the
  // ANALYTE, but Location opens on "All sites" — comparing the reviewer's sites
  // against each other for one analyte is the whole reason the mode exists
  // (the reference design's "All THM sites (D1–D4)").  Pinning it to the focus
  // row's single site would draw exactly one line.  The focus site is kept in
  // preferredSite and used the moment the reviewer flips to mode 2, where a
  // single location IS the constant.
  useEffect(() => {
    if (!points || seeded || points.length === 0) return;
    const matchAnalyte =
      pickerAnalytes.find((a) => focusAnalyte && a.toLowerCase().includes(focusAnalyte.toLowerCase())) ??
      pickerAnalytes[0] ?? analytes[0] ?? '';
    const matchSite =
      sites.find((s) => focusSite && s.toLowerCase().includes(focusSite.toLowerCase())) ?? sites[0] ?? '';
    setAnalyte(matchAnalyte);
    setPreferredAnalyte(matchAnalyte);
    setPreferredSite(matchSite);
    setMode('analyte');
    // With a row selection, auto-scope Location to the SELECTED sampling
    // location(s): exactly one → that single location (the "trend a parameter at
    // the same sampling location" core case); several → the "Selected locations"
    // subset.  With NO selection, open on "All sites" so the pickers browse
    // freely (the manual fallback).  (Client requirement 2026-08-03.)
    if (hasSelection && matchedSelectedSites.length === 1) {
      setSite(matchedSelectedSites[0] as string);
    } else if (hasSelection && matchedSelectedSites.length > 1) {
      setSite(SELECTED_SITES);
    } else {
      setSite('');        // "All sites" — every series on one shared MCL
    }
    setSeeded(true);
  }, [points, seeded, analytes, pickerAnalytes, sites, focusAnalyte, focusSite, hasSelection, matchedSelectedSites]);

  // ── series + chart data ───────────────────────────────────────────────────
  // In 'analyte' mode a series is a SITE (native units, shared MCL line).
  // In 'site' mode a series is an ANALYTE (normalised to % of its own MCL).
  const { chartData, series, months, yLabel, mclLine, mclLabel, mclLegend, pointMeta } = useMemo(() => {
    const all = points ?? [];
    const empty = {
      chartData: [] as Record<string, string | number | null>[],
      series: [] as { key: string; label: string; color: string; dash?: string }[],
      months: [] as string[],
      yLabel: '',
      mclLine: null as number | null,
      mclLabel: '',
      mclLegend: '',
      pointMeta: new Map<string, PointMeta>(),
    };
    if (all.length === 0) return empty;

    // site === '' → every site; SELECTED_SITES → only the reviewer's ticked
    // locations; otherwise a single named location.
    const siteInScope = (s: string) =>
      site === '' ? true : site === SELECTED_SITES ? matchedSelectedSites.includes(s) : s === site;
    const inScope =
      mode === 'analyte'
        ? all.filter((p) => p.analyte === analyte && siteInScope(p.site))
        : all.filter((p) => p.site === site && (analyte === '' ? regulated.includes(p.analyte) : p.analyte === analyte));
    if (inScope.length === 0) return empty;

    // The axis is the WHOLE rolling window, not just the months that have data:
    // a site sampled 4 times must read as 4 points in a 12-month record, not as
    // a full-looking 4-month chart.  The window COUNTS BACK FROM TODAY (client
    // 2026-08-03) — it ends at the current month, matching TrendReader's
    // GETDATE() anchor server-side, so a location with no recent sampling shows
    // trailing empty months.  Guard: never end BEFORE the newest data month, so
    // if the browser's "today" and the server's differ by a month no point ever
    // falls off the right edge.
    const present = Array.from(new Set(inScope.map((p) => p.month))).sort();
    const nowMonth = monthKeyOf(new Date());
    const newest = present.length ? (present[present.length - 1] as string) : '';
    const anchorMonth = newest && newest > nowMonth ? newest : nowMonth;
    const monthList = present.length ? monthSequence(anchorMonth, WINDOW_MONTHS) : present;
    const keys = Array.from(new Set(inScope.map((p) => (mode === 'analyte' ? p.site : p.analyte)))).sort((a, b) =>
      a.localeCompare(b),
    );

    // month → seriesKey → the LATEST reading that month, chosen by MAX collect
    // date (client 2026-08-03).  The chart plots the most-recent result per month,
    // NOT the monthly mean — and it uses the SAME max-collect-date rule as
    // rollUp() (the summary table's "Latest"), so the tooltip and the grid agree
    // exactly, in BOTH modes, regardless of API row order or several samples in a
    // month.  Falls back to the month bucket when a point has no collect date.
    // Resolve each analyte's MCL ONCE and apply it to ALL of that analyte's points
    // in 'site' mode.  An MCL is a regulatory CONSTANT per analyte, but the source
    // data carries a null MCL on SOME individual rows.  Requiring a per-row MCL made
    // the chart DROP the latest such reading and plot a staler one, so the chart's
    // point disagreed with the summary table's "Latest" (client 2026-08-04 — Aluminum
    // at D1: the newest 07/22 reading (0.048) had a null MCL, so the chart showed the
    // older 07/15 (0.043 → 21.5%) while the table showed 0.048 → 24%).  Mirrors
    // rollUp()'s `ordered.find(p => p.mcl !== null)` so the chart and table use the
    // SAME analyte MCL (constant, so iteration order is immaterial).
    const mclByAnalyte = new Map<string, number>();
    if (mode === 'site') {
      inScope.forEach((p) => {
        if (p.mcl !== null && !mclByAnalyte.has(p.analyte)) mclByAnalyte.set(p.analyte, p.mcl);
      });
    }

    const cell = new Map<string, PointMeta>();
    inScope.forEach((p) => {
      const k = `${p.month}|${mode === 'analyte' ? p.site : p.analyte}`;
      // In 'site' mode every series is normalised so 100% IS the limit, for
      // every analyte — the only way one axis can carry µg/L and mg/L at once.
      // Use the analyte's resolved MCL (not the per-row p.mcl, which may be null).
      const v = mode === 'analyte'
        ? p.value
        : (pctOfMcl(p.value, mclByAnalyte.get(p.analyte) ?? p.mcl) ?? NaN);
      if (!Number.isFinite(v)) return;
      const cd = p.collectDate ?? p.month;
      const cur = cell.get(k);
      // Keep the latest by date, carrying the NATIVE value + unit (for the tooltip,
      // which shows native value/unit even in 'site' mode's % view).
      if (cur === undefined || cd >= cur.cd) cell.set(k, { v, cd, nv: p.value, units: p.units ?? '' });
    });

    const data = monthList.map((m) => {
      const row: Record<string, string | number | null> = { month: m, label: monthShort(m) };
      keys.forEach((k) => {
        const c = cell.get(`${m}|${k}`);
        // roundForChart (not a fixed 1-decimal): keeps 3 sig figs below 1 so trace
        // analytes (Turbidity 0.136, Manganese 0.05) don't collapse to 0.1 on the
        // chart/tooltip while the summary table shows their true value.
        row[k] = c !== undefined ? roundForChart(c.v) : null;
      });
      return row;
    });

    const units = inScope.find((p) => p.units)?.units ?? '';
    const analyteMcl = inScope.find((p) => p.mcl !== null)?.mcl ?? null;

    return {
      chartData: data,
      series: keys.map((k, i) => ({ key: k, label: k, color: seriesColor(i), dash: seriesDash(i) })),
      months: monthList,
      pointMeta: cell,
      yLabel: mode === 'analyte' ? `Result (${units || 'value'})` : '% of MCL',
      mclLine: mode === 'analyte' ? analyteMcl : 100,
      // In-chart annotation beside the dashed line vs the legend row caption —
      // the mockup words these differently ("MCL 80 µg/L" on the line,
      // "MCL (80 µg/L)" in the legend).
      mclLabel:
        mode === 'analyte'
          ? analyteMcl !== null
            ? `MCL ${trimZeros(analyteMcl)} ${units}`.trim()
            : ''
          : 'MCL = 100%',
      mclLegend:
        mode === 'analyte'
          ? analyteMcl !== null
            ? `MCL (${trimZeros(analyteMcl)} ${units})`.trim()
            : ''
          : 'MCL (100%)',
    };
  }, [points, mode, analyte, site, regulated, matchedSelectedSites]);

  // ── summary table rows ────────────────────────────────────────────────────
  const summary = useMemo(() => {
    const all = points ?? [];
    if (all.length === 0) return [] as { key: string; label: string; stat: Stat }[];
    const siteInScope = (s: string) =>
      site === '' ? true : site === SELECTED_SITES ? matchedSelectedSites.includes(s) : s === site;
    if (mode === 'analyte') {
      const scope = all.filter((p) => p.analyte === analyte && siteInScope(p.site));
      const keys = Array.from(new Set(scope.map((p) => p.site))).sort((a, b) => a.localeCompare(b));
      return keys.map((k) => ({ key: k, label: k, stat: rollUp(scope.filter((p) => p.site === k)) }));
    }
    const scope = all.filter(
      (p) => p.site === site && (analyte === '' ? regulated.includes(p.analyte) : p.analyte === analyte),
    );
    const keys = Array.from(new Set(scope.map((p) => p.analyte))).sort((a, b) => a.localeCompare(b));
    return keys.map((k) => ({ key: k, label: k, stat: rollUp(scope.filter((p) => p.analyte === k)) }));
  }, [points, mode, analyte, site, regulated, matchedSelectedSites]);

  // The MOST-RECENT month across ALL rows (not the first row's) — the historic
  // union brings in archive-only sites whose latest sample predates the live
  // batch, so picking one row's latest would mislabel the column (e.g. showing
  // an old archive month instead of the newest live month).
  const latestMonth = summary.reduce<string | null>(
    (mx, s) => (s.stat.latestMonth && (!mx || s.stat.latestMonth > mx) ? s.stat.latestMonth : mx),
    null,
  );
  // Distinguish "no data" from "data exists but no MCL is configured".  The
  // second is a DATA GAP the lab can fix (Testparameter.MCL), and it is common:
  // on AlpacaLims_GoLive 2026-08-01 none of Alkalinity / Calcium / Conductivity
  // / Free Ammonia / pH / Total Ammonia carries an MCL.  Saying "no results"
  // there would send someone hunting for missing samples that are actually
  // present.
  const noMclInScope = useMemo(() => {
    if (!points) return false;
    const siteInScope = (s: string) =>
      site === '' ? true : site === SELECTED_SITES ? matchedSelectedSites.includes(s) : s === site;
    const scope =
      mode === 'site'
        ? points.filter((p) => p.site === site && (analyte === '' || p.analyte === analyte))
        : points.filter((p) => p.analyte === analyte && siteInScope(p.site));
    return scope.length > 0 && scope.every((p) => p.mcl === null);
  }, [points, mode, site, analyte, matchedSelectedSites]);

  // Y axis: a ceiling that ALWAYS clears the MCL line, plus an explicit round
  // tick ladder (see axisTicks — 78 data / 80 MCL yields 0,15,…,90 like the
  // reference).  In "% of MCL" mode the floor is 100 so the limit line is
  // on-chart even when every analyte sits far below it → the 0-120% axis.
  const yAxis = useMemo(() => {
    const vals = chartData.flatMap((row) =>
      series.map((s) => row[s.key]).filter((v): v is number => typeof v === 'number'),
    );
    const dataMax = vals.length ? Math.max(...vals) : 0;
    // "% of MCL" mode is already normalised, so the 100% line is always on
    // scale — only the native-units mode can have a far-away limit.
    if (mode === 'site') return { ...axisTicks(Math.max(dataMax, EXCEED_PCT) * 1.12), mclOnScale: true, mclPinned: false };
    return chooseAxis(dataMax, mclLine);
  }, [chartData, series, mclLine, mode]);

  // Changing what is charted invalidates the hidden set — its keys belonged to
  // the previous scope, and a stale key would silently hide a new series.
  useEffect(() => {
    setHiddenSeries(new Set());
    setHoverSeries(null);
  }, [mode, analyte, site]);

  const firstMonth = months[0];
  const lastMonth = months[months.length - 1];
  const rangeLabel = firstMonth && lastMonth ? `${monthLong(firstMonth)} – ${monthLong(lastMonth)}` : '';
  const hasPartialLraa = summary.some((s) => s.stat.lraaPartial);
  const showsManySeries = series.length > MAX_END_LABELS;

  // ── Trend SUMMARY-TABLE column model (global grid features: right-click menu,
  // Hide / Show All / Column Chooser, drag-reorder, resize, persistence) — the
  // SAME shared hooks the hand-rolled Data Center grids use. The table is a
  // computed summary with no sort, so the menu's Sort items are disabled
  // (sortable:false). Columns are mode-dependent; one persistKey is fine because
  // useColumnManager ignores keys not present in the current column set.
  const trendCols = useMemo(() => {
    const mid =
      mode === 'analyte'
        ? [
            { key: 'mean12', label: `${WINDOW_MONTHS}-mo mean` },
            { key: 'lraa', label: `LRAA${summary[0]?.stat.units ? ` (${summary[0].stat.units})` : ''}` },
          ]
        : [
            { key: 'units', label: 'Units' },
            { key: 'mcl', label: 'MCL' },
          ];
    return [
      { key: 'label', label: mode === 'analyte' ? 'SamplingLocation' : 'Analyte' },
      { key: 'latest', label: `Latest${latestMonth ? ` (${monthSlash(latestMonth)})` : ''}` },
      ...mid,
      { key: 'pct', label: '% of MCL' },
      { key: 'status', label: 'Status' },
    ];
  }, [mode, latestMonth, summary]);
  const trendCm = useColumnManager(trendCols, (c) => c.key, (c) => c.label, 'sdms-trend');
  const trendMenu = useHeaderMenu({ sort: { key: null, dir: null }, setSortExplicit: () => {} }, trendCm.columnMenu);

  // ── summary-table export projection ───────────────────────────────────────
  // Mirrors the <td> renderers below cell-for-cell (same r1 / r0 / trimZeros /
  // statusOf helpers), over the VISIBLE columns in the user's current order —
  // so the exported file matches the table exactly. The chart is not exportable;
  // this table under it is what "Export to" writes on the Trend tab.
  const trendCellText = (row: { label: string; stat: Stat }, key: string): string => {
    switch (key) {
      case 'label':  return row.label;
      case 'latest': return r1(row.stat.latest);
      case 'mean12': return r1(row.stat.mean12);
      case 'lraa':   return `${r1(row.stat.lraa)}${row.stat.lraaPartial ? '*' : ''}`;
      case 'units':  return row.stat.units || '—';
      case 'mcl':    return trimZeros(row.stat.mcl);
      case 'pct':    return r0(row.stat.pct);
      case 'status': return statusOf(row.stat.pct);
      default:       return '';
    }
  };
  const visibleTrendCols = trendCm.visibleColumns;
  useEffect(() => {
    if (!onExportData) return;
    onExportData({
      headers: visibleTrendCols.map((c) => c.label),
      rows: summary.map((row) => visibleTrendCols.map((c) => trendCellText(row, c.key))),
    });
    // trendCellText is a stable pure mapping over its args — re-running on the
    // data it reads (columns + rows) is what keeps the export in step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onExportData, visibleTrendCols, summary]);

  // Hide the pickers + mode toggle when there is nothing to pick BETWEEN — an
  // empty Parameter list, "All sites (0)" and a mode switch that changes nothing
  // are noise on a batch that can never chart (qualitative test, or no reviewed
  // history at all).  They stay visible when points DO exist and merely the
  // current selection is empty, because there the pickers are the way out.
  const hasAnyData = !qualitative && (points ?? []).length > 0;

  // ── selection warnings (client requirement 2026-08-03) ────────────────────
  // When the reviewer opens Trend with a row selection that CAN'T be charted as
  // asked, say why via the shared notification service — never leave them with a
  // silently-wrong or empty chart.  Fires once per mount (the tab remounts on
  // every open), after the data has loaded and the seed has run.
  useEffect(() => {
    if (loading || warnedRef.current || !hasSelection) return;
    warnedRef.current = true;
    if (qualitative) return; // the empty state already explains this outright
    // (a) Rows span more than one parameter — Trend charts one at a time.
    if (selectedAnalytes.length > 1) {
      notify.warning(
        `The selected rows cover ${selectedAnalytes.length} parameters (${selectedAnalytes.join(', ')}). ` +
          `Trend charts one parameter at a time — showing ${analyte || selectedAnalytes[0]}. ` +
          `Select rows for a single parameter, or use the Parameter picker.`,
      );
    }
    // (b) Nothing to trend for the selection.
    if (matchedSelectedSites.length === 0) {
      if (selectedSites.length === 0 && sitelessSelected.length > 0) {
        // QC-only selection — the ticked rows carry NO sampling location at all.
        // A QC sample can never be trended, so this fires regardless of whether
        // the batch has other history (reliably reproducible on any numeric batch).
        notify.warning(
          `The selected row${sitelessSelected.length > 1 ? 's' : ''} (${sitelessSelected.join(', ')}) ` +
            `${sitelessSelected.length > 1 ? 'are' : 'is'} QC samples with no sampling location, so there is ` +
            `no location trend. Select a field sample, or use the Location picker.`,
        );
      } else if ((points ?? []).length > 0) {
        // Field locations WERE selected, but none has reviewed 12-month history.
        notify.warning(
          `No reviewed ${WINDOW_MONTHS}-month history was found for the selected location(s). ` +
            `Use the Location picker to choose another, or clear the selection.`,
        );
      }
    } else if (matchedSelectedSites.length < selectedSites.length) {
      // (c) Partial — some selected locations have history, some don't.
      /* WARNING, not info — so it lands in the CENTERED MODAL, not the top banner.
         §18.6's two-tier rule puts warnings in the blocking dialog and reserves the
         transient banner for routine success/info; this message says part of the
         analyst's SELECTION was silently dropped from the chart, which they have to
         see before reading the trend. The three sibling branches above already use
         notify.warning — this one was the odd one out (client 2026-08-25). */
      notify.warning(
        `Some selected location(s) have no reviewed ${WINDOW_MONTHS}-month history — ` +
          `showing the ${matchedSelectedSites.length} that do.`,
      );
    }
  }, [
    loading,
    hasSelection,
    qualitative,
    selectedAnalytes,
    analyte,
    matchedSelectedSites,
    selectedSites,
    sitelessSelected,
    points,
    notify,
  ]);

  // Is the view still scoped to the reviewer's row selection (vs. them switching
  // the Location picker to "All sites" or another single location)?  Drives the
  // "auto-scoped" note so it never claims a scope the chart isn't actually using.
  const scopeSites =
    hasSelection && matchedSelectedSites.length > 0
      ? site === SELECTED_SITES
        ? matchedSelectedSites
        : matchedSelectedSites.includes(site)
          ? [site]
          : []
      : [];
  const scopedToSelection = scopeSites.length > 0;

  // "one site · many analytes" footnote helpers (client 2026-08-03) — mirror the
  // reference design's "(TTHM 80 µg/L, Nitrate 10 mg/L …) … TTHM is trending
  // toward its MCL" but built from THIS site's ACTUAL analytes + limits, so it is
  // never mock text. `summary` in 'site' mode is one row per analyte at the site.
  const mclRows = summary.filter((s) => s.stat.mcl !== null && s.stat.mcl !== undefined);
  const siteMclExamples = mclRows
    .slice(0, 3)
    .map((s) => `${shortAnalyte(s.label)} ${trimZeros(s.stat.mcl)} ${s.stat.units}`.trim());
  const moreMclExamples = mclRows.length > siteMclExamples.length;
  // The analyte closest to its MCL at this site — drives the closing takeaway.
  const worstAnalyte = summary
    .filter((s) => s.stat.pct !== null && Number.isFinite(s.stat.pct as number))
    .reduce<{ label: string; pct: number } | null>((best, s) => {
      const p = s.stat.pct as number;
      return !best || p > best.pct ? { label: shortAnalyte(s.label), pct: p } : best;
    }, null);

  // The custom hover tooltip is the module-level <TrendTip> component below —
  // passed to <Tooltip content={...}> as an ELEMENT (the canonical Recharts
  // pattern; Recharts injects active/payload into the clone).

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="dv-pane dv-on">
      <div className="dv-mini">
        <button className="dv-rf" onClick={() => setReloadTick((n) => n + 1)} disabled={loading}>
          ↻ {loading ? 'Loading…' : 'Refresh'}
        </button>
        <span className="dv-gap" />
        {error && <span className="dv-note" style={{ color: 'var(--ai-red)' }}>Failed to load: {error}</span>}
        {/* Grid corner actions (Export · Maximize) — on the REFRESH row, the same
            line every other tab puts them on (client 2026-08-18). */}
        {actions}
      </div>

      {/* tr-wrap fills the pane; the PLOT inside is capped (.tr-plot max-width)
          and LEFT-aligned so it starts at the card's left edge, in line with the
          pickers / summary table / Review Log grid — the cap still stops a wide
          monitor from flattening every series into a near-horizontal line. */}
      <div className="dv-scroll tr-scroll">
       <div className="tr-wrap">
        {/* ── picker row.  The FIRST picker is always the dimension being held
            constant, so the sentence reads left-to-right in both modes.
            The PICKERS are suppressed when the batch has nothing to chart, but
            the row itself still renders so the corner actions (Export ·
            Maximize) stay put — a batch with no history is exactly when a
            reviewer may still want to maximize the pane. ── */}
        {hasAnyData && (
        <div className="tr-controls">
          {hasAnyData && (mode === 'analyte' ? (
            <>
              <label className="tr-lbl">Parameter</label>
              <select className="tr-sel" data-testid="tr-param-select" value={analyte} onChange={(e) => setAnalyte(e.target.value)}>
                {pickerAnalytes.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
              <label className="tr-lbl">Location</label>
              <select className="tr-sel" value={site} onChange={(e) => setSite(e.target.value)}>
                {/* When the reviewer ticked rows for more than one location, the
                    selected subset is a first-class choice (and the seed default). */}
                {hasSelection && matchedSelectedSites.length > 1 && (
                  <option value={SELECTED_SITES}>Selected locations ({matchedSelectedSites.length})</option>
                )}
                {/* Only the sites that have THIS analyte — the count then equals
                    the summary-table row count. */}
                <option value="">All sites ({sitesForAnalyte.length})</option>
                {sitesForAnalyte.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </>
          ) : (
            <>
              <label className="tr-lbl">Location</label>
              <select className="tr-sel" value={site} onChange={(e) => setSite(e.target.value)}>
                {sites.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <label className="tr-lbl">Parameter</label>
              <select className="tr-sel" data-testid="tr-param-select" value={analyte} onChange={(e) => setAnalyte(e.target.value)}>
                {/* REGULATED analytes at the selected SITE only (client 2026-08-06):
                    'site' mode's axis is "% of MCL", so an analyte without an MCL
                    (Alkalinity, Calcium, Color, organic surrogates, …) can't be
                    charted here — listing the site's full cross-test analyte set
                    contradicted the "All regulated analytes" label + the chart/table/
                    legend, which already show only the regulated ones. Count matches
                    the summary-table rows. */}
                <option value="">All regulated analytes ({regulatedAtSite.length})</option>
                {regulatedAtSite.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </>
          ))}

          <span className="tr-spacer" />
          {hasAnyData && (
          <div className="tr-toggle" role="group" aria-label="Trend mode">
            <button
              className={mode === 'analyte' ? 'tr-on' : ''}
              onClick={() => {
                setMode('analyte');
                setSite('');
                // Mode 2 sets analyte='' to mean "all regulated analytes", which
                // is not a valid selection in mode 1 — a blank <select> value
                // silently displayed the first option while the state stayed
                // empty, so Location read "All sites (0)" and the chart showed
                // "No reviewed results". Restore a concrete analyte on the way
                // back (the one the reviewer arrived with, when known).
                if (!analyte) setAnalyte(preferredAnalyte || regulated[0] || analytes[0] || '');
              }}
            >
              One analyte · many sites
            </button>
            <button
              className={mode === 'site' ? 'tr-on' : ''}
              onClick={() => {
                // Mode 2 holds ONE location constant, so resolve a concrete site
                // (SELECTED_SITES / '' are not valid single-site values here).
                const target =
                  site && site !== SELECTED_SITES ? site : matchedSelectedSites[0] || preferredSite || sites[0] || '';
                // Illogical action: "% of MCL" needs an MCL — warn when the target
                // location has none configured (the chart would otherwise be empty).
                const regAtTarget = new Set(
                  (points ?? []).filter((p) => p.site === target && p.mcl !== null).map((p) => p.analyte),
                );
                if (target && regAtTarget.size === 0) {
                  notify.warning(
                    `“${target}” has no analyte with an MCL configured, so “% of MCL” can't be charted for it. ` +
                      `Configure an MCL for its analytes, or use “one analyte · many sites”.`,
                  );
                }
                setMode('site');
                setSite(target);
                setAnalyte('');
              }}
            >
              One site · many analytes
            </button>
          </div>
          )}
        </div>
        )}

        {/* Auto-scope note — makes the selection-driven behaviour transparent:
            what the chart is scoped to, and how to switch to manual browsing
            (client requirement 2026-08-03). */}
        {scopedToSelection && chartData.length > 0 && (
          <div className="tr-scope-note">
            <i className="ti ti-info-circle" aria-hidden />
            <span>
              Auto-scoped to your selected location{scopeSites.length > 1 ? 's' : ''}{' '}
              (<b>{scopeSites.join(', ')}</b>)
              {analyte ? (
                <>
                  {' '}for <b>{analyte}</b>
                </>
              ) : null}
              . Uncheck the Batch Results rows, or use the pickers above, to trend a different parameter or location.
            </span>
          </div>
        )}

        {chartData.length === 0 ? (
          <div className="tr-empty">
            {loading ? (
              'Loading trend…'
            ) : qualitative ? (
              <>
                {testName ? <b>{testName}</b> : 'This test'} reports <b>qualitative</b> results (e.g.{' '}
                <b>Present / Absent</b>), not numbers, so there is no numeric trend to plot.
                <br />
                Trend, MCL check and LRAA apply only to tests that produce a measured value.
              </>
            ) : (points ?? []).length === 0 ? (
              <>
                No reviewed numeric results were found for this batch's sampling locations in the last {WINDOW_MONTHS} months.
                <br />
                Trend is built from prior <i>reviewed</i> results — approved where the SDMS approval stage is in use,
                otherwise validated.
              </>
            ) : noMclInScope && mode === 'site' ? (
              <>
                No analyte at this location has an <b>MCL</b> configured, so results cannot be normalised to “% of
                MCL”.
                <br />
                Set <code>Testparameter.MCL</code> for these analytes to enable this view, or use{' '}
                <b>“one analyte · many sites”</b>, which plots native units and needs no MCL.
              </>
            ) : (
              `No reviewed results for this selection in the last ${WINDOW_MONTHS} months.`
            )}
          </div>
        ) : (
          <>
            {/* .tr-chart = the bordered OUTER BOX, full grid width (client
                2026-08-01: "outside box is grid size").
                .tr-plot   = the plot itself, capped and centred INSIDE that box
                so the series keep readable proportions on a wide monitor
                ("graph only center alignment"). */}
            <div className="tr-chart">
             {/* Plot LEFT, legend RIGHT (client 2026-08-05): the legend moved from a
                 row BELOW the box into the space to the right of the capped plot, so
                 the chart gains vertical height. tr-chart-inner is a flex row that
                 WRAPS → the legend drops below the plot on narrow widths. */}
             <div className="tr-chart-inner">
              <div className="tr-plot">
               {/* Taller now the legend no longer sits below it (client 2026-08-05). */}
               <ResponsiveContainer width="100%" height={showsManySeries ? 460 : 400}>
                <LineChart data={chartData} margin={{ top: 16, right: 74, bottom: 34, left: 16 }}>
                  <CartesianGrid stroke="#eef1f6" vertical={false} />
                  <XAxis
                    dataKey="label"
                    fontSize={11.5}
                    tick={{ fill: '#6b7280' }}
                    label={{
                      value: `Sample month (${rangeLabel})`,
                      position: 'insideBottom',
                      offset: -20,
                      fontSize: 11.5,
                      fill: '#6b7280',
                    }}
                  />
                  <YAxis
                    domain={[0, yAxis.max]}
                    ticks={yAxis.ticks}
                    allowDataOverflow={false}
                    fontSize={11.5}
                    tick={{ fill: '#6b7280' }}
                    tickFormatter={(v: number) => (mode === 'site' ? `${v}%` : `${v}`)}
                    label={{ value: yLabel, angle: -90, position: 'insideLeft', fontSize: 11.5, fill: '#6b7280' }}
                  />
                  {/* Tooltip — the DEFAULT Recharts tooltip (which renders reliably;
                      a custom `content` element rendered INVISIBLE in this overlay
                      layout — 2026-08-04), ENRICHED via formatter/labelFormatter to
                      show the client's fields: header = parameter (or site) ONLY —
                      the sample month was removed 2026-08-07 (each row already shows
                      its own exact collection date, which differ within a month, so a
                      single month in the heading was redundant/misleading); each row =
                      series · native value + unit (+ % of MCL in 'site' mode) · date. */}
                  <Tooltip
                    labelFormatter={() => (mode === 'analyte' ? shortAnalyte(analyte) : site)}
                    formatter={(v: number, name: string, item: { payload?: Record<string, unknown> }) => {
                      const month = item?.payload?.month as string | undefined;
                      const meta = month ? pointMeta.get(`${month}|${name}`) : undefined;
                      const nativeStr = meta ? r1(meta.nv) : r1(v);
                      const unit = meta?.units ?? '';
                      const dateStr = meta && meta.cd.length >= 10 ? fmtLocalDate(meta.cd) : '';
                      let val = `${nativeStr}${unit ? ` ${unit}` : ''}`;
                      if (mode === 'site') val += ` · ${r0(v)} of MCL`;
                      if (dateStr) val += ` · ${dateStr}`;
                      return [val, mode === 'analyte' ? name : shortAnalyte(name)];
                    }}
                    contentStyle={{ fontSize: 12 }}
                  />
                  {/* The MCL limit line is ALWAYS drawn when the analyte has an
                      MCL (client 2026-08-07). It sits at its TRUE height when it
                      fits the axis; when the limit is far above the readings the
                      axis stays scaled to the DATA (so the lines stay readable)
                      and the line is PINNED to the top edge, labelled "(above
                      range)" — see chooseAxis. */}
                  {mclLine !== null && yAxis.mclOnScale && (
                    <ReferenceLine
                      y={yAxis.mclPinned ? yAxis.max : mclLine}
                      stroke={MCL_COLOR}
                      strokeDasharray="6 4"
                      label={{
                        value: yAxis.mclPinned ? `${mclLabel} ▲ above range` : mclLabel,
                        position: 'insideTopRight',
                        fontSize: 11,
                        fill: MCL_COLOR,
                      }}
                    />
                  )}
                  {series.filter((s) => !hiddenSeries.has(s.key)).map((s) => (
                    <Line
                      key={s.key}
                      type="linear"
                      dataKey={s.key}
                      name={s.label}
                      stroke={s.color}
                      strokeDasharray={s.dash}
                      // Hovering a legend entry pushes every OTHER series back,
                      // which is the only way to follow one line through a tight
                      // overlapping band.
                      strokeOpacity={hoverSeries && hoverSeries !== s.key ? 0.15 : 1}
                      strokeWidth={hoverSeries === s.key ? 3.2 : 2}
                      // Larger dots so sparsely-sampled series (a site with only
                      // 2-3 monthly points) are easy to see AND to hover for the
                      // tooltip (client 2026-08-04).
                      dot={{ r: 3.4, fill: s.color, strokeWidth: 0,
                             fillOpacity: hoverSeries && hoverSeries !== s.key ? 0.15 : 1 }}
                      activeDot={{ r: 6 }}
                      connectNulls
                      isAnimationActive={false}
                    />
                  ))}
                  {/* End-of-line direct labels for EVERY visible line, with vertical
                      collision avoidance (client 2026-08-06: "end of graph lines
                      denotes a sitename"). One <Customized> overlay sees all lines'
                      end pixels at once so labels DODGE apart instead of piling up —
                      which is why the old per-line labels were capped at 5 series. */}
                  <Customized component={EndLabelsOverlay} />
                </LineChart>
              </ResponsiveContainer>
              </div>
             {/* ── legend — now to the RIGHT of the plot (client 2026-08-05: was a
                 row BELOW the box; moved into the unused space beside the capped
                 plot so the chart gains vertical height). A vertical list that wraps
                 BELOW the plot on narrow widths. Still the interactive filter: hover
                 = highlight, click = hide/show. */}
             <div className="tr-legend tr-legend-side">
              {series.map((s) => (
                <span
                  key={s.key}
                  className={`tr-lg-item${hiddenSeries.has(s.key) ? ' tr-lg-off' : ''}`}
                  role="button"
                  tabIndex={0}
                  title={hiddenSeries.has(s.key) ? 'Click to show this series' : 'Click to hide this series'}
                  onMouseEnter={() => setHoverSeries(s.key)}
                  onMouseLeave={() => setHoverSeries(null)}
                  onClick={() =>
                    setHiddenSeries((prev) => {
                      const next = new Set(prev);
                      if (next.has(s.key)) next.delete(s.key);
                      else next.add(s.key);
                      return next;
                    })
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setHiddenSeries((prev) => {
                        const next = new Set(prev);
                        if (next.has(s.key)) next.delete(s.key);
                        else next.add(s.key);
                        return next;
                      });
                    }
                  }}
                >
                  <i style={s.dash
                    ? { borderTop: `3px dashed ${s.color}`, background: 'none', height: 0 }
                    : { background: s.color }} />
                  {legendLabel(s.label, mode, points ?? [])}
                </span>
              ))}
              {mclLine !== null && mclLegend && (
                <span>
                  <i className="tr-lg-dash" style={{ borderTopColor: MCL_COLOR }} />
                  {/* The MCL line is always drawn; when the limit is far above the
                      data it's pinned to the top edge, so the legend says so. */}
                  {yAxis.mclPinned ? `${mclLegend} — above range` : mclLegend}
                </span>
              )}
              {series.length > 3 && (
                <span className="tr-lg-hint">
                  hover to highlight · click to hide
                  {hiddenSeries.size > 0 && (
                    <>
                      {' · '}
                      <button className="tr-lg-reset" onClick={() => setHiddenSeries(new Set())}>
                        show all ({hiddenSeries.size} hidden)
                      </button>
                    </>
                  )}
                </span>
              )}
             </div>{/* .tr-legend (right column) */}
            </div>{/* .tr-chart-inner (plot + legend row) */}
            </div>{/* .tr-chart (bordered box) */}

            {/* ── summary table ─────────────────────────────────────────── */}
            {/* Caption — makes it clear this table is a LOCATION's cross-test
                history over the window, NOT this batch's own results (client
                2026-08-03: the analyte list confused a Calcium batch's reviewer). */}
            <div className="tr-tbl-cap">
              {mode === 'analyte' ? (
                <>
                  Reviewed <b>{shortAnalyte(analyte)}</b> results across all sampling locations · last {WINDOW_MONTHS} months (live + historic archive).
                </>
              ) : (
                <>
                  Regulated analytes measured at <b>{site}</b> across <b>all tests</b> · last {WINDOW_MONTHS} months (reviewed results, live + historic archive)
                  — not just this batch.
                </>
              )}
            </div>
            <table className="dv-tbl tr-tbl">
              <thead>
                <tr>
                  {/* Array-driven from the column model → drag-reorder + hide
                      keep header and body in lock-step; position:relative anchors
                      the shared .adg-resize handle to each column's right edge. */}
                  {trendCm.visibleColumns.map((c) => (
                    <th
                      key={c.key}
                      className={trendCm.dragClass(c.key)}
                      style={{
                        position: 'relative',
                        // A user resize wins; otherwise the balanced default keyed
                        // by column (with table-layout:fixed) shares the width evenly.
                        ...(trendCm.colWidth(c.key)
                          ? { width: trendCm.colWidth(c.key), minWidth: trendCm.colWidth(c.key) }
                          : TREND_COL_WIDTH[c.key]
                            ? { width: TREND_COL_WIDTH[c.key] }
                            : {}),
                      }}
                      onContextMenu={(e) => trendMenu.openHeaderMenu(e, { field: c.key, label: c.label, sortable: false })}
                      {...trendCm.dragProps(c.key)}
                    >
                      {c.label}
                      <span className="adg-resize" onMouseDown={(e) => trendCm.startResize(c.key, e)} onClick={(e) => e.stopPropagation()} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {summary.map((row) => {
                  const st = statusOf(row.stat.pct);
                  // One cell per column key; the row renders only the VISIBLE
                  // columns in the user's order (same source as the header).
                  const cell: Record<string, ReactNode> = {
                    label: <td key="label">{row.label}</td>,
                    latest: <td key="latest">{r1(row.stat.latest)}</td>,
                    mean12: <td key="mean12">{r1(row.stat.mean12)}</td>,
                    lraa: (
                      <td key="lraa">
                        {r1(row.stat.lraa)}
                        {row.stat.lraaPartial && <span className="tr-partial" title="Fewer than 4 results available">*</span>}
                      </td>
                    ),
                    units: <td key="units">{row.stat.units || '—'}</td>,
                    // MCL at natural precision — drop trailing ".0" (80.0 → 80) but
                    // keep real decimals (0.2, 0.01); never force-rounded (client 2026-08-06).
                    mcl: <td key="mcl">{trimZeros(row.stat.mcl)}</td>,
                    // % of MCL as WHOLE percent (client 2026-08-06 reference: 74.1/80
                    // = 92.6% → "93%"). r0 rounds + appends % and guards null → '—'.
                    pct: <td key="pct">{r0(row.stat.pct)}</td>,
                    // No MCL → no status: a bare dash, not an empty grey pill (which
                    // reads as a broken badge rather than "not applicable").
                    status: (
                      <td key="status">
                        {st === '—' ? (
                          <span className="tr-na-dash">—</span>
                        ) : (
                          <span className={`tr-pill ${STATUS_CLASS[st]}`}>{st}</span>
                        )}
                      </td>
                    ),
                  };
                  return <tr key={row.key}>{trendCm.visibleColumns.map((c) => cell[c.key])}</tr>;
                })}
              </tbody>
            </table>
            {trendCm.chooser}
            {trendMenu.headerMenu}

            {/* NOTE: the "No MCL is configured for this parameter" info bar that
                used to sit here was removed at the client's request
                (2026-08-01) — the em dashes in the % of MCL / Status columns are
                considered self-explanatory.  The equivalent message in "one site
                · many analytes" mode is KEPT, because there it replaces an empty
                chart and without it the card would explain nothing. */}

            {/* ── explanatory footnote: says WHY the chosen mode looks the way
                it does, so a reviewer isn't left to infer the normalisation. ── */}
            <p className="tr-foot">
              {mode === 'analyte' ? (
                <>
                  <b>Multiple sites &amp; parameters:</b> the Trend view opens scoped to the result row selected in
                  Batch Results (its site + analyte). <b>Location</b> and <b>Parameter</b> pickers pivot without leaving
                  validation.
                  In <b>“one analyte · many sites”</b> mode (shown) every series shares one axis and MCL because the
                  analyte is the same; in <b>“one site · many analytes”</b> mode results are normalised to{' '}
                  <b>% of MCL</b> since analytes have different scales and limits.{' '}
                  Trend, MCL check and locational running annual average (LRAA) are computed from prior <i>approved</i>{' '}
                  results for the analyte + location.
                </>
              ) : (
                <>
                  <b>One site · many analytes:</b> because analytes carry different units and limits
                  {siteMclExamples.length > 0 ? (
                    <> ({siteMclExamples.join(', ')}{moreMclExamples ? ' …' : ''})</>
                  ) : null}
                  , each result is plotted as <b>% of its own MCL</b> so a single shared axis is meaningful — the{' '}
                  <b>100%</b> line is the regulatory limit for every series.
                  {worstAnalyte ? (
                    worstAnalyte.pct >= WATCH_PCT ? (
                      <>
                        {' '}At <b>{site}</b>, <b>{worstAnalyte.label}</b> is trending toward its MCL (
                        {r0(worstAnalyte.pct)}) while the others hold well below.
                      </>
                    ) : (
                      <>
                        {' '}At <b>{site}</b>, every analyte holds well below its MCL (highest is{' '}
                        <b>{worstAnalyte.label}</b> at {r0(worstAnalyte.pct)}).
                      </>
                    )
                  ) : null}
                </>
              )}
              {hasPartialLraa && <> An asterisk marks an LRAA averaged over fewer than {LRAA_WINDOW} results.</>}
            </p>
          </>
        )}
       </div>
      </div>
    </div>
  );
}

/** "TTHM (Total Trihalomethanes)" → "TTHM".  The parenthetical expansion is
 *  useful in a picker but makes a legend row unreadable. */
function shortAnalyte(name: string): string {
  return name.replace(/\s*\(.*\)\s*$/, '').trim() || name;
}

/** Legend caption.
 *  • "one analyte · many sites" → just the SITE name (e.g. "Water Plant A - 2310
 *    North Park"). Every series is the SAME analyte — already named in the Parameter
 *    picker + the caption — so appending "(analyte)" to every row was redundant and
 *    overflowed / clipped the legend column off the right edge (client 2026-08-06).
 *  • "one site · many analytes" → "TTHM (MCL 80 µg/L)" — each analyte's own limit,
 *    so a reader can convert a percentage back to native units. */
function legendLabel(key: string, mode: TrendMode, points: Point[]): string {
  if (mode === 'analyte') return key;
  const p = points.find((x) => x.analyte === key && x.mcl !== null);
  return p && p.mcl !== null ? `${shortAnalyte(key)} (MCL ${trimZeros(p.mcl)} ${p.units})`.trim() : shortAnalyte(key);
}

/** Direct-label form for a line's end — shows the site/analyte name IN FULL, only
 *  dropping a redundant leading "Site " prefix. Earlier trimming of a " - suffix"
 *  and a trailing "(qualifier)" was WRONG for descriptive lab site names (client
 *  2026-08-06): it collapsed "Kingwood (golf)" → "Kingwood" (ambiguous with
 *  "Kingwood Marina"), "West Fork - HWY 59" → "West Fork", and "Treasure Island -
 *  Magnolia Point" → "Treasure Island". The end label must uniquely identify its
 *  line, so it now carries the whole name (the legend adds the analyte annotation;
 *  the end label does not). */
function endLabelShort(name: string): string {
  return name.replace(/^Site\s+/i, '').trim() || name;
}

/** Recharts <Customized> overlay: an end-of-line direct label (ring + short name)
 *  for EVERY visible line, at that line's last real point, DODGED vertically so
 *  labels near the same value don't pile up (client 2026-08-06: "end of graph lines
 *  denotes a sitename"). One overlay — instead of the old per-<Line> label, which
 *  was capped at 5 series because it couldn't see its neighbours — lets it place all
 *  labels together. Reads the chart's internal formattedGraphicalItems (each line's
 *  computed pixel points) + the plot offset; renders nothing if that shape is ever
 *  absent (defensive). */
type FgiEntry = {
  props?: {
    points?: Array<{ x?: number; y?: number; value?: number | null }>;
    name?: string;
    stroke?: string;
  };
  item?: { props?: { name?: string; stroke?: string } };
};
function EndLabelsOverlay(props: {
  formattedGraphicalItems?: FgiEntry[];
  offset?: { top?: number; height?: number };
}) {
  const items = Array.isArray(props.formattedGraphicalItems) ? props.formattedGraphicalItems : [];
  const top = props.offset?.top ?? 0;
  const height = props.offset?.height ?? 0;

  const labels = items
    .map((entry) => {
      const pts = entry?.props?.points ?? [];
      let last: { x?: number; y?: number; value?: number | null } | undefined;
      for (let i = pts.length - 1; i >= 0; i--) {
        const pt = pts[i];
        if (pt && pt.value != null && typeof pt.x === 'number' && typeof pt.y === 'number') { last = pt; break; }
      }
      if (!last || typeof last.x !== 'number' || typeof last.y !== 'number') return null;
      const name = entry?.props?.name ?? entry?.item?.props?.name ?? '';
      const color = entry?.props?.stroke ?? entry?.item?.props?.stroke ?? '#666';
      return { x: last.x, y: last.y, name: endLabelShort(name), color };
    })
    .filter((l): l is { x: number; y: number; name: string; color: string } => l !== null);
  if (labels.length === 0) return <g />;

  // Dodge overlapping labels to a minimum vertical gap, clamped inside the plot.
  labels.sort((a, b) => a.y - b.y);
  const gap = 13;
  const minY = top + 6;
  const maxY = top + height - 6;
  let prev = -Infinity;
  for (const l of labels) {
    if (l.y < minY) l.y = minY;
    if (l.y - prev < gap) l.y = prev + gap;
    prev = l.y;
  }
  // If pushing down overflowed the bottom, pull the tail back up to fit.
  const lastLabel = labels[labels.length - 1];
  if (lastLabel && lastLabel.y > maxY) {
    let next = maxY + gap;
    for (let i = labels.length - 1; i >= 0; i--) {
      const li = labels[i];
      if (!li) continue;
      if (li.y > next - gap) li.y = next - gap;
      next = li.y;
    }
  }

  return (
    <g>
      {labels.map((l, i) => (
        <g key={i} className="tr-end-label">
          <circle cx={l.x} cy={l.y} r={3.5} fill="#fff" stroke={l.color} strokeWidth={1.6} />
          <text x={l.x + 8} y={l.y + 3.5} fill={l.color} fontSize={11} fontWeight={600}>{l.name}</text>
        </g>
      ))}
    </g>
  );
}
